import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Award, Download, Eye, Loader2, Printer, Search, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { getCurrentUser } from '@/data/users';

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('authToken') || localStorage.getItem('token');
const api = async (path, opts = {}) => {
  const res = await fetch(`${API}/api/certificates${path}`, { headers: { Authorization: `Bearer ${token()}` }, ...opts });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Request failed (${res.status})`); }
  return res;
};
const fetchBlobUrl = async (path) => URL.createObjectURL(await (await api(path)).blob());

export default function CertificatesPage() {
  const user = getCurrentUser();
  const isAdmin = user?.email?.toLowerCase() === 'roytari@spc.edu.vu' || user?.role === 'admin';
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [year, setYear] = useState('');
  const [q, setQ] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const fileRef = useRef(null);

  const loadSchools = useCallback(() => api('/templates').then((r) => r.json()).then((s) => { setSchools(s); setSchoolId((c) => c || s[0]?.school_id || ''); }).catch((e) => toast.error(e.message)), []);
  useEffect(() => { loadSchools(); }, [loadSchools]);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    api(`/students?school_id=${schoolId}${year ? `&year=${year}` : ''}`).then((r) => r.json()).then(setStudents).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, [schoolId, year]);

  const school = schools.find((s) => s.school_id === schoolId);
  const hasAnyTemplate = !!school && (school.has_template || (school.year_templates || []).length > 0);
  const canCert = (s) => !!school && (school.has_template || (school.year_templates || []).includes(s.year));
  const years = [...new Set(students.filter((s) => !hasAnyTemplate || canCert(s)).map((s) => s.year).filter(Boolean))].sort((a, b) => a - b);
  const shown = students.filter((s) => (!hasAnyTemplate || canCert(s)) && s.name.toLowerCase().includes(q.toLowerCase()));

  const view = async (s) => {
    setBusy(s.id);
    try { setPreview({ name: s.name, url: await fetchBlobUrl(`/student/${s.id}.png`) }); } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };
  const download = async (s) => {
    setBusy(s.id);
    try {
      const url = await fetchBlobUrl(`/student/${s.id}.png`);
      const a = document.createElement('a'); a.href = url; a.download = `${s.name.replace(/[^A-Za-z0-9]+/g, '_')}_Certificate.png`; a.click();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };
  const printAll = async () => {
    setBusy('bulk');
    try {
      const url = await fetchBlobUrl(`/bulk.pdf?school_id=${schoolId}${year ? `&year=${year}` : ''}`);
      window.open(url, '_blank');
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };
  const uploadTemplate = async (file) => {
    if (!file) return;
    const form = new FormData(); form.append('file', file);
    try {
      await api(`/templates/${schoolId}${year ? `?year=${year}` : ''}`, { method: 'POST', body: form });
      toast.success(`Template saved for ${school?.school_name}${year ? ` (Year ${year} only)` : ''}`);
      loadSchools();
    } catch (e) { toast.error(e.message); }
  };

  const removeTemplate = async () => {
    const label = year ? `the Year ${year} design` : 'the school-wide design';
    if (!window.confirm(`Remove ${label} for ${school?.school_name}? Certificates for those students will no longer be available until a new design is uploaded.`)) return;
    try {
      await api(`/templates/${schoolId}${year ? `?year=${year}` : ''}`, { method: 'DELETE' });
      toast.success(`Removed ${label} for ${school?.school_name}`);
      loadSchools();
    } catch (e) { toast.error(e.message); }
  };
  const canRemove = !!school && (year ? (school.year_templates || []).includes(Number(year)) : school.has_template);

  return (
    <div className="space-y-6" data-testid="certificates-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Award className="h-7 w-7 text-amber-600" /> Certificates of Completion</h1>
          <p className="text-muted-foreground mt-1">Each student's certificate is generated from the school's template with their name. Parents receive it in the Parent Portal once the report card is published.</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => uploadTemplate(e.target.files?.[0])} data-testid="template-file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!schoolId} className="gap-2" data-testid="upload-template-btn"><Upload className="h-4 w-4" /> {year ? `Upload Year ${year} template` : (school?.has_template ? 'Replace school template' : 'Upload template')}</Button>
              {canRemove && <Button variant="outline" onClick={removeTemplate} className="gap-2 text-red-700 border-red-200 hover:bg-red-50" data-testid="remove-template-btn"><Trash2 className="h-4 w-4" /> {year ? `Remove Year ${year} design` : 'Remove school design'}</Button>}
            </>
          )}
          <Button onClick={printAll} disabled={!hasAnyTemplate || !shown.filter(canCert).length || busy === 'bulk'} className="gap-2 bg-emerald-700 hover:bg-emerald-800" data-testid="print-all-btn">
            {busy === 'bulk' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Print all {year ? `Year ${year}` : ''} (PDF)
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <select value={schoolId} onChange={(e) => { setSchoolId(e.target.value); setYear(''); }} className="h-10 border rounded-md px-3 bg-background text-sm" data-testid="cert-school-select">
          {schools.map((s) => <option key={s.school_id} value={s.school_id}>{s.school_name}{(s.has_template || s.year_templates?.length) ? '' : ' — no template'}</option>)}
        </select>
        <select value={year} onChange={(e) => setYear(e.target.value)} className="h-10 border rounded-md px-3 bg-background text-sm" data-testid="cert-year-select">
          <option value="">All years</option>
          {years.map((y) => <option key={y} value={y}>Year {y}</option>)}
        </select>
        <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search student…" className="pl-9" data-testid="cert-search" /></div>
      </div>

      {school?.year_templates?.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="year-templates-note">
          Year-specific designs in use: {school.year_templates.map((y) => `Year ${y}`).join(', ')}. {school.has_template ? 'Other years use the school template.' : 'Other years have no certificate yet.'}
        </p>
      )}

      {school && !hasAnyTemplate && (
        <Card className="border-amber-300 bg-amber-50"><CardContent className="py-4 text-sm text-amber-900" data-testid="no-template-notice">
          No certificate template has been uploaded for {school.school_name} yet{isAdmin ? ' — upload a PNG/JPG with the school logo and signatures above.' : '. Ask the admin (Roy Tari) to upload one.'}
        </CardContent></Card>
      )}

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Students {loading ? <Loader2 className="inline h-4 w-4 animate-spin ml-1" /> : <span className="font-normal text-muted-foreground">({shown.length})</span>}</CardTitle></CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y max-h-[60vh] overflow-auto" data-testid="cert-student-list">
            {shown.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`cert-row-${s.id}`}>
                <div className="min-w-0"><p className="font-medium truncate">{s.name}</p>{s.year && <Badge variant="secondary" className="text-xs">Year {s.year}</Badge>}</div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => view(s)} disabled={!canCert(s) || busy === s.id} className="gap-1" data-testid={`cert-view-${s.id}`}>{busy === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} View</Button>
                  <Button size="sm" variant="outline" onClick={() => download(s)} disabled={!canCert(s) || busy === s.id} className="gap-1" data-testid={`cert-download-${s.id}`}><Download className="h-3.5 w-3.5" /> Download</Button>
                </div>
              </li>
            ))}
            {!loading && shown.length === 0 && <li className="p-6 text-center text-sm text-muted-foreground">No students found.</li>}
          </ul>
        </CardContent>
      </Card>

      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) setPreview(null); }}>
        <DialogContent className="max-w-5xl" data-testid="cert-preview-dialog">
          <DialogHeader><DialogTitle>{preview?.name} — Certificate</DialogTitle></DialogHeader>
          {preview && <img src={preview.url} alt={`Certificate for ${preview.name}`} className="w-full rounded shadow" />}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { const w = window.open(preview.url, '_blank'); w?.addEventListener('load', () => w.print()); }} className="gap-2" data-testid="cert-print-one"><Printer className="h-4 w-4" /> Print</Button>
            <Button asChild className="gap-2"><a href={preview?.url} download={`${preview?.name?.replace(/[^A-Za-z0-9]+/g, '_')}_Certificate.png`}><Download className="h-4 w-4" /> Download</a></Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
